import React, { useCallback, useEffect, useState } from 'react';
import { Alert, CircularProgress, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BillingHistory } from '../components/subscription/BillingHistory';
import { CancelSubscriptionDialog } from '../components/subscription/CancelSubscriptionDialog';
import { DowngradeDialog } from '../components/subscription/DowngradeDialog';
import { InviteFriendsCard } from '../components/subscription/InviteFriendsCard';
import { PlanCard } from '../components/subscription/PlanCard';
import { ScheduledChangeBanner } from '../components/subscription/ScheduledChangeBanner';
import { PurchaseDialog, type SelectedPurchase } from '../components/subscription/PurchaseDialog';
import { TokenPackSection } from '../components/subscription/TokenPackSection';
import { useNotification } from '../hooks/useNotification';
import {
  cancelSubscription,
  getBillingHistory,
  getCurrentSubscription,
  getPackages,
  getTokenPacks,
  resumeSubscription,
  subscribe,
} from '../services/api';
import type {
  BillingHistoryItem,
  CurrentSubscription,
  Package,
  TokenPack,
  TokenPackPrice,
} from '../types';
import { formatDate } from '../utils/formatDate';
import { getPlanAction } from '../utils/planChange';

export const SubscriptionPage: React.FC = () => {
  const { success, error } = useNotification();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const highlightFeature = searchParams.get('feature');
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'VND'>(
    i18n.language === 'vi' ? 'VND' : 'USD'
  );
  const [packages, setPackages] = useState<Package[]>([]);
  const [tokenPacks, setTokenPacks] = useState<TokenPack[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [downgradeTarget, setDowngradeTarget] = useState<Package | null>(null);
  const [selectedPurchase, setSelectedPurchase] = useState<SelectedPurchase | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { refreshSubscription, refreshQuota } = useAuth();
  const currentPackageCode = currentSubscription?.package?.code?.toUpperCase();
  const currentPackageName = currentSubscription?.package?.name;

  useEffect(() => {
    setSelectedCurrency(i18n.language === 'vi' ? 'VND' : 'USD');
  }, [i18n.language]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [packagesRes, subscriptionRes, historyRes, tokenPacksRes] = await Promise.all([
        getPackages(),
        getCurrentSubscription(),
        getBillingHistory(),
        getTokenPacks(),
      ]);

      if (!packagesRes.success || !packagesRes.data) {
        throw new Error(packagesRes.error || 'Failed to load packages');
      }

      setPackages(packagesRes.data);
      setCurrentSubscription(subscriptionRes.success ? (subscriptionRes.data ?? null) : null);
      setBillingHistory(historyRes.success && historyRes.data ? historyRes.data : []);
      setTokenPacks(
        tokenPacksRes.success && tokenPacksRes.data ? tokenPacksRes.data.tokenPacks : []
      );
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => {
    void fetchAllData();
  }, [fetchAllData]);

  // Sidebar badge and quota live in AuthContext, so refresh them with the page data.
  const reloadAll = async () => {
    await fetchAllData();
    void refreshSubscription();
    void refreshQuota();
  };

  const isFreePackage = (pkg: Package) => {
    return (
      pkg.code.toUpperCase() === 'FREE' ||
      pkg.price === 0 ||
      (pkg.prices ?? []).some((price) => Number(price.amountMinor) === 0)
    );
  };

  const freePackageName = packages.find(isFreePackage)?.name ?? t('subscription.plan.freePlan');

  const handleCancelSubscription = async () => {
    if (
      !currentSubscription?.publicId ||
      currentPackageCode === 'FREE' ||
      currentSubscription.scheduledChange
    ) {
      error(t('subscription.freeCancelDenied', 'Free plan cannot be cancelled.'));
      setCancelDialogOpen(false);
      return;
    }

    setActionLoading(true);
    setCancelDialogOpen(false);

    try {
      const result = await cancelSubscription(currentSubscription.publicId);
      if (result.success) {
        success(
          t('subscription.cancelScheduled', {
            endAt: formatDate(currentSubscription.endAt, i18n.language),
            name: freePackageName,
          })
        );
        await reloadAll();
      } else {
        error(result.error || t('subscription.cancelFailed', 'Failed to cancel subscription'));
      }
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = async (pkg: Package) => {
    // Priced plans are bought through checkout (coupon + SePay), not granted directly.
    const payable = pkg.prices?.some(
      (p) =>
        p.currency === 'VND' &&
        p.billingPeriod === 'month' &&
        p.isActive !== false &&
        Number(p.amountMinor) > 0
    );
    if (payable) {
      setSelectedPurchase({ kind: 'package', pkg, billingPeriod: 'month' });
      return;
    }
    setActionLoading(true);
    try {
      const result = await subscribe(pkg.publicId);
      if (result.success) {
        success(t('subscription.upgraded', { name: pkg.name }));
        await reloadAll();
      } else {
        error(result.error || t('subscription.updateFailed', 'Failed to update subscription'));
      }
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to update subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDowngrade = (pkg: Package) => {
    setDowngradeTarget(pkg);
  };

  const handleConfirmDowngrade = async () => {
    if (!downgradeTarget) return;

    setActionLoading(true);
    setDowngradeTarget(null);
    try {
      const result = await subscribe(downgradeTarget.publicId);
      if (result.success) {
        const subscription = result.data as CurrentSubscription | undefined;
        const effectiveAt =
          subscription?.scheduledChange?.effectiveAt ?? currentSubscription?.endAt;
        success(
          t('subscription.downgradeScheduled', {
            name: downgradeTarget.name,
            date: effectiveAt ? formatDate(effectiveAt, i18n.language) : '',
          })
        );
        await reloadAll();
      } else {
        error(result.error || t('subscription.updateFailed', 'Failed to update subscription'));
      }
    } catch (err) {
      error(err instanceof Error ? err.message : 'Failed to update subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    if (!currentSubscription?.publicId || !currentPackageName) return;

    setActionLoading(true);
    try {
      const result = await resumeSubscription(currentSubscription.publicId);
      if (result.success) {
        success(t('subscription.resumed', { name: currentPackageName }));
        await reloadAll();
      } else {
        error(result.error || t('subscription.resumeFailed'));
      }
    } catch (err) {
      error(err instanceof Error ? err.message : t('subscription.resumeFailed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleFreePurchaseActivated = async () => {
    const wasPackage = selectedPurchase?.kind === 'package';
    setSelectedPurchase(null);
    success(
      wasPackage
        ? t('subscription.payment.planActivated', 'Your plan is now active.')
        : t(
            'subscription.advanceBalance.purchaseComplete',
            'Purchase complete. Your advance balance has been topped up.'
          )
    );
    await reloadAll();
  };

  const showPurchaseDialog = (tokenPack: TokenPack, price: TokenPackPrice) => {
    if (!tokenPack.isPurchasable) {
      error(
        tokenPack.blockedReason === 'daily_limit'
          ? t(
              'subscription.advanceBalance.daily_limit',
              'Daily purchase limit reached. Try again tomorrow.'
            )
          : t('subscription.advanceBalance.monthly_limit', 'Monthly purchase limit reached.')
      );
      return;
    }
    setSelectedPurchase({ kind: 'token_pack', tokenPack, price });
  };

  const formatPrice = (amountMinor: string | number, currency: string) => {
    const value = Number(amountMinor);
    const displayValue = currency === 'USD' ? value / 100 : value;

    return new Intl.NumberFormat(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'VND' ? 0 : 2,
    }).format(displayValue);
  };

  if (loading) {
    return <SubscriptionSkeleton />;
  }

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-8">
      {actionLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-lg bg-white p-6 shadow-lg dark:bg-slate-800">
            <CircularProgress />
            <p className="mt-4 text-gray-900 dark:text-slate-100">
              {t('common.processing', 'Processing...')}
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900 dark:text-slate-100">
            {t('subscription.choosePlan', 'Choose Your Plan')}
          </h1>
          <p className="text-gray-600 dark:text-slate-400">
            {t('subscription.subtitle', 'Select the perfect plan for your needs')}
          </p>
        </div>
        <CurrencyToggle value={selectedCurrency} onChange={setSelectedCurrency} />
      </div>

      {highlightFeature && (
        <Alert severity="info" className="mb-6">
          {t('packageFeatures.subscriptionHint', {
            feature: t(`packageFeatures.names.${highlightFeature}`, highlightFeature),
          })}
        </Alert>
      )}

      {currentSubscription?.scheduledChange && (
        <ScheduledChangeBanner subscription={currentSubscription} onResume={handleResume} />
      )}

      <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {packages.map((pkg) => (
          <PlanCard
            key={pkg.publicId}
            package={pkg}
            selectedCurrency={selectedCurrency}
            action={getPlanAction(pkg, currentSubscription, packages)}
            scheduledChange={
              currentSubscription?.scheduledChange?.packagePublicId === pkg.publicId ||
              currentSubscription?.package?.publicId === pkg.publicId
                ? currentSubscription?.scheduledChange
                : null
            }
            highlighted={!!highlightFeature && (pkg.features ?? []).includes(highlightFeature)}
            onUpgrade={handleUpgrade}
            onDowngrade={handleDowngrade}
            onCancel={() => setCancelDialogOpen(true)}
            onResume={handleResume}
          />
        ))}
      </div>

      <TokenPackSection
        tokenPacks={tokenPacks}
        selectedCurrency={selectedCurrency}
        onSelect={showPurchaseDialog}
        formatPrice={formatPrice}
      />

      <InviteFriendsCard />

      <div className="mt-12">
        <h2 className="mb-6 text-2xl font-bold text-gray-900 dark:text-slate-100">
          {t('subscription.billingHistory', 'Billing History')}
        </h2>
        <BillingHistory history={billingHistory} />
      </div>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        packageName={currentSubscription?.package?.name}
        freePackageName={freePackageName}
        endAt={currentSubscription?.endAt}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelSubscription}
      />
      <DowngradeDialog
        open={Boolean(downgradeTarget)}
        targetPackage={downgradeTarget}
        currentPackageName={currentPackageName}
        endAt={currentSubscription?.endAt}
        onClose={() => setDowngradeTarget(null)}
        onConfirm={handleConfirmDowngrade}
      />
      <PurchaseDialog
        selectedPurchase={selectedPurchase}
        initialCoupon={searchParams.get('coupon')}
        onClose={() => setSelectedPurchase(null)}
        onFreeActivated={() => void handleFreePurchaseActivated()}
        formatPrice={formatPrice}
      />
    </div>
  );
};

const SubscriptionSkeleton: React.FC = () => (
  <div className="mx-auto max-w-7xl p-4 md:p-8">
    <div className="mb-8">
      <Skeleton variant="text" width={200} height={40} />
      <Skeleton variant="text" width={300} height={24} />
    </div>
    <div className="mb-12 grid grid-cols-1 gap-6 md:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="rectangular" height={400} className="rounded-lg" />
      ))}
    </div>
    <Skeleton variant="rectangular" height={300} className="rounded-lg" />
  </div>
);

const CurrencyToggle: React.FC<{
  value: 'USD' | 'VND';
  onChange: (value: 'USD' | 'VND') => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-slate-700 dark:bg-slate-800">
    {(['USD', 'VND'] as const).map((currency) => (
      <button
        key={currency}
        type="button"
        onClick={() => onChange(currency)}
        className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all ${
          value === currency
            ? 'bg-white text-blue-600 shadow-sm dark:bg-slate-700 dark:text-blue-400'
            : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-200'
        }`}
      >
        {currency === 'USD' ? 'USD ($)' : 'VND (₫)'}
      </button>
    ))}
  </div>
);
